import type {
  DealerParkEventSource,
  DealerParkedEvent,
} from '../../models/entities/dealer-api.model';
import type {
  EventsParkedResponse,
  ParkedEventRow,
  ParkEventResponse,
} from '../../models/entities/events-parked.model';
import { formatQueueTimestamp } from '../dto-mappers';

const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function parseParkedUtcInstant(value: string): Date {
  const trimmed = value.trim();
  if (ISO_WITHOUT_OFFSET.test(trimmed)) {
    return new Date(`${trimmed}Z`);
  }
  return new Date(trimmed);
}

function resolveParkedDisplayTimeZone(timezone?: string | null): string {
  const requested = timezone?.trim();
  if (requested) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: requested }).format(new Date());
      return requested;
    } catch {
      // Invalid IANA — parked tables show UTC, not the browser zone.
    }
  }
  return 'UTC';
}

function formatParkedTableTimestamp(value: string, timezone?: string | null): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }

  const date = parseParkedUtcInstant(trimmed);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return formatQueueTimestamp(date.toISOString(), resolveParkedDisplayTimeZone(timezone));
}

function computeExpiresAt(parkedAtIso: string, durationMinutes: number): string | null {
  if (durationMinutes <= 0) {
    return null;
  }
  const parkedAtMs = Date.parse(parkedAtIso);
  if (Number.isNaN(parkedAtMs)) {
    return null;
  }
  return new Date(parkedAtMs + durationMinutes * 60_000).toISOString();
}

/** Maps Dealer parked event rows into the Agent UI parked table contract. */
export function mapDealerParkedEventToRow(
  event: DealerParkEventSource,
  durationMinutes = 0,
  timezone?: string | null,
): ParkedEventRow {
  const parkedAtIso = event.parkedDateTime?.trim() || '';
  const firstAlarmIso = event.firstAlarmDateTime ?? event.createdDateTime ?? '';
  return {
    id: event.eventId,
    siteId: event.siteId,
    firstAlarmDetected: firstAlarmIso ? formatParkedTableTimestamp(firstAlarmIso, timezone) : '',
    location: event.siteName?.trim() || `Site ${event.siteId}`,
    parkedAt: parkedAtIso ? formatParkedTableTimestamp(parkedAtIso, timezone) : '',
    parkedBy: event.parkedByUserName?.trim() ?? '',
    durationMinutes,
    expiresAt: parkedAtIso ? computeExpiresAt(parkedAtIso, durationMinutes) : null,
  };
}

export function mapDealerParkedEventsToResponse(
  events: DealerParkedEvent[] | null | undefined,
): EventsParkedResponse {
  const items = (events ?? []).map((event) =>
    mapDealerParkedEventToRow(event, 0, event.siteTimeZone),
  );
  return { totalCount: items.length, items };
}

/** Maps Scalar `POST /events/{eventId}/park` `{ event }` into the UI park contract. */
export function mapDealerParkEventToResponse(
  event: DealerParkEventSource | null | undefined,
  durationMinutes: number,
  timezone?: string | null,
): ParkEventResponse {
  if (!event?.eventId?.trim()) {
    throw new Error('Park response did not include an event');
  }
  return {
    success: true,
    parked: mapDealerParkedEventToRow(event, durationMinutes, event.siteTimeZone ?? timezone),
  };
}
